/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, IAssessmentApplicationEx, yupPhoneTestOptions } from '../../common';
import {
  AddressPicker,
  AddressSchema,
  Checkbox,
  DatePickerProvider,
  ErrorScope,
  FormContext,
  FormPage,
  FormPageContext,
  IconButton,
  Label,
  PhoneInput,
  SecurePay,
  SecurePayMethods,
  Select,
  TextInput,
  TokenisedCard,
} from 'acma-spa';
import { Acma_amateurradioassessment_statuscode, Acma_amateurradioassessmentmethods, Acma_amateurradioassessmenttypes, getApiClient } from '../../api';
import { PrivacyDeclaration } from './PrivacyDeclaration';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

export const schema: yup.ObjectSchema<IAssessmentApplicationEx> = yup.object({
  id: yup.string(),
  referenceNumber: yup.string(),
  title: yup.string(),
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  otherName: yup.string(),
  emailAddress: yup.string().email().required().label('Email address'),
  phone1: yup.string().required().label('Contact phone').test(yupPhoneTestOptions),
  phone2: yup.string().label('Other phone').test(yupPhoneTestOptions),
  address1: AddressSchema.default(undefined).required('Address is required'),
  createdOn: yup.date(),
  assessorName: yup.string().label('Assessor Name'),
  type: yup.mixed<Acma_amateurradioassessmenttypes>().oneOf(Object.values(Acma_amateurradioassessmenttypes)).required().label('Qualification'),
  method: yup.mixed<Acma_amateurradioassessmentmethods>().oneOf(Object.values(Acma_amateurradioassessmentmethods)).required().label('Method'),
  confirmation: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
  tokenisedCard: yup.string(),
  dob: yup.date().required().label('Date of Birth').max(new Date(), "This must be earlier or on today"),
  status: yup.mixed<Acma_amateurradioassessment_statuscode>().oneOf(Object.values(Acma_amateurradioassessment_statuscode)).optional(),
});

export interface CertificateApplicationPageProps extends ClientApiProps {
  ctx: FormContext<IAssessmentApplicationEx>;
}

/** Assessment Types provided as an array of Key-Value pairs */
const assessmentTypes = Object.entries(Acma_amateurradioassessmenttypes).map((entry) => {
  const [label, value] = entry;
  return { label, value };
});

export const CertificateApplicationPage: React.FC<CertificateApplicationPageProps> = ({ ctx, ...props }) => {
  const securePayRef = useRef<SecurePayMethods>(null);
  const [price, setPrice] = useState<number>();
  const getPrice = async () => {
    const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    setPrice(await client.getPrice());
  };

  useEffect(() => {
    if (!price) getPrice();
  }, []);

  return (
    <FormPage<IAssessmentApplicationEx>
      ctx={ctx}
      schema={schema}
      onNext={async () => {
        if (securePayRef.current != null) {
          securePayRef.current.tokenise();
          return false;
        } else {
          return true;
        }
      }}>
      {(ctx: FormPageContext<IAssessmentApplicationEx>) => {
        const { model, setModel, errors } = ctx;
        const [tokenisedCard, setTokenisedCard] = useState<TokenisedCard | null>(null);
        const invoicePriceAsText = price?.toFixed(2);

        const requiresPayment: boolean =
          ctx.readOnly === false && (price ?? 0) > 0 && model.method != Acma_amateurradioassessmentmethods.PriorLearningRecognition;

        useEffect(() => {
          if (!tokenisedCard) {
            return;
          }
          ctx.onNavigate({ ...model, tokenisedCard: tokenisedCard.token }, true, true);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [tokenisedCard]);

        return (
          <>
            <PrivacyDeclaration {...props} />

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="title" label="Title" error={errors.title}>
                  <TextInput value={model.title ?? ''} onChange={(title) => setModel({ title })} readonly={ctx.readOnly} maxLength={400} />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="firstName" label="First Name" required={true} error={errors.firstName}>
                  <TextInput value={model.firstName ?? ''} onChange={(firstName) => setModel({ firstName })} maxLength={149} readonly={ctx.readOnly} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="lastName" label="Last Name" required={true} error={errors.lastName}>
                  <TextInput value={model.lastName ?? ''} onChange={(lastName) => setModel({ lastName })} maxLength={149} readonly={ctx.readOnly} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="otherName" label="Other Name" error={errors.otherName}>
                  <TextInput value={model.otherName ?? ''} onChange={(otherName) => setModel({ otherName })} maxLength={149} readonly={ctx.readOnly} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label
                  id="dob"
                  required={true}
                  error={errors.dob}
                  label={
                    <>
                      Date of Birth{' '}
                      <IconButton
                        onClick={() => {}}
                        label="Date of birth is required for Harmonised Amateur Radio Examination Certificates (HAREC) recognition"
                        mode={faInfoCircle}
                      />
                    </>
                  }>
                  <DatePickerProvider
                    value={model.dob}
                    onChange={(v) => setModel({ dob: v ?? undefined })}
                    className="mt-2"
                    size="lg"
                    disabled={ctx.readOnly}
                    maxDate={new Date()}
                  />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="emailAddress" label="Email address" required={true} error={errors.emailAddress}>
                  <TextInput value={model.emailAddress ?? ''} onChange={(emailAddress) => setModel({ emailAddress })} readonly={ctx.readOnly} maxLength={400} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="phone1" label="Contact phone" required={true} error={errors.phone1}>
                  <PhoneInput value={model.phone1 ?? ''} onChange={(phone1) => setModel({ phone1 })} readonly={ctx.readOnly} maxLength={100} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="phone2" label="Other phone" error={errors.phone2}>
                  <PhoneInput value={model.phone2 ?? ''} onChange={(phone2) => setModel({ phone2 })} readonly={ctx.readOnly} maxLength={100} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col mb-3">
                <Label id="address1" label="Contact address" required={true} error={errors.address1}>
                  <AddressPicker value={model.address1 ?? null} onChange={(v) => setModel({ address1: v ?? undefined })} disabled={ctx.readOnly} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-3 mb-3">
                <Label id="qualificationLevel" label="Qualification level attained" error={errors?.type}>
                  <Select value={model.type ?? undefined} onChange={() => {}} options={assessmentTypes} disabled={true} />
                </Label>
              </div>
              <div className="col-sm-9 mb-3">
                <Label id="assessorName" label="Name of Accredited Assessor who supervised the exam" error={errors?.assessorName}>
                  <TextInput readonly={true} value={model.assessorName ?? ''} onChange={() => {}} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col mb-3">
                <Label id="dateAttained" label="Date the Qualification was attained" error={errors?.createdOn}>
                  <DatePickerProvider value={model.createdOn} onChange={() => {}} disabled={true} />
                </Label>
              </div>
            </div>

            {requiresPayment && (
              <>
                <h2 className="my-2">Payment details</h2>
                <h4>Total: ${invoicePriceAsText} AUD</h4>
                <SecurePay
                  clientId={props.securePayClientId ?? ''}
                  merchantCode={props.securePayMerchantCode ?? ''}
                  uri={`${props.securePaySrc}`}
                  onLoadComplete={() => {}}
                  onTokeniseError={() => ctx.onLoading(null)}
                  onTokeniseSuccess={(v) => {
                    ctx.onLoading(null);
                    setTokenisedCard(v);
                  }}
                  ref={securePayRef}
                />
              </>
            )}

            <hr />

            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation" error={errors?.confirmation}>
                  <Checkbox
                    id="confirmation"
                    label="I confirm that the information provided is true and correct."
                    checked={ctx.readOnly ? true : model.confirmation ?? false}
                    onChange={(v) => setModel({ confirmation: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>
          </>
        );
      }}
    </FormPage>
  );
};
